/**
 * Endpoints de **Gestión → Administración → Comprobantes**
 * (`/api/gestion/administracion/comprobantes*`).
 *
 * Porta el mantenimiento de los comprobantes administrativos del panel anterior
 * (`Administration::invoices`): listado, detalle, aprobación a la cola de SUNAT,
 * actualización de importes, consulta de estado, nota de crédito y anulación.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  AnulacionRespuesta,
  AprobacionRespuesta,
  CatalogosNotaRespuesta,
  CatalogosCrearRespuesta,
  ComprobantesRespuesta,
  CrearComprobanteRespuesta,
  DetalleComprobanteRespuesta,
  EstadoConsultadoRespuesta,
  FiltrosComprobantes,
  NotaEmitidaRespuesta,
  PeticionCrearComprobante,
  PrecioActualizadoRespuesta,
} from './comprobantes.types';

const CLAVE = 'gestion';

/** Línea con el precio nuevo que se envía al actualizar importes. */
export interface LineaPrecio {
  id: number;
  precio: number;
  total: number;
}

/** Convierte los filtros en cadena de consulta. */
function cadenaDe(filtros: { [clave: string]: string | number | undefined }): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '') continue;

    parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const comprobantesKeys = {
  listado: (filtros: FiltrosComprobantes) => [CLAVE, 'comprobantes', 'listado', filtros] as const,
  detalle: (id: number | null) => [CLAVE, 'comprobantes', 'detalle', id] as const,
  catalogoNota: (id: number | null) => [CLAVE, 'comprobantes', 'nota', id] as const,
  catalogoCrear: (tipo: string) => [CLAVE, 'comprobantes', 'crear-catalogos', tipo] as const,
};

export const comprobantesApi = {
  listar: (filtros: FiltrosComprobantes, signal?: AbortSignal): Promise<ComprobantesRespuesta> =>
    apiClient.get<ComprobantesRespuesta>(
      `/api/gestion/administracion/comprobantes${cadenaDe({ ...filtros })}`,
      { signal },
    ),

  detalle: (id: number, signal?: AbortSignal): Promise<DetalleComprobanteRespuesta> =>
    apiClient.get<DetalleComprobanteRespuesta>(`/api/gestion/administracion/comprobantes/${id}`, { signal }),

  catalogoNota: (id: number, signal?: AbortSignal): Promise<CatalogosNotaRespuesta> =>
    apiClient.get<CatalogosNotaRespuesta>(
      `/api/gestion/administracion/comprobantes/${id}/nota`,
      { signal },
    ),

  catalogosCrear: (tipo: string, signal?: AbortSignal): Promise<CatalogosCrearRespuesta> =>
    apiClient.get<CatalogosCrearRespuesta>(
      `/api/gestion/administracion/comprobantes/crear/catalogos${cadenaDe({ tipo })}`,
      { signal },
    ),

  crear: (datos: PeticionCrearComprobante): Promise<CrearComprobanteRespuesta> =>
    apiClient.post<CrearComprobanteRespuesta>('/api/gestion/administracion/comprobantes', {
      body: datos,
    }),

  aprobar: (comprobantes: number[]): Promise<AprobacionRespuesta> =>
    apiClient.post<AprobacionRespuesta>('/api/gestion/administracion/comprobantes/aprobar', {
      body: { comprobantes },
    }),

  reparar: (): Promise<{ reparados: number; mensaje: string }> =>
    apiClient.post<{ reparados: number; mensaje: string }>(
      '/api/gestion/administracion/comprobantes/reparar',
      { body: {} },
    ),

  actualizarPrecio: (
    id: number,
    datos: { detalles: LineaPrecio[]; total: number; retencion_porcentaje: number },
  ): Promise<PrecioActualizadoRespuesta> =>
    apiClient.post<PrecioActualizadoRespuesta>(
      `/api/gestion/administracion/comprobantes/${id}/precio`,
      { body: datos },
    ),

  consultarEstado: (id: number): Promise<EstadoConsultadoRespuesta> =>
    apiClient.post<EstadoConsultadoRespuesta>(
      `/api/gestion/administracion/comprobantes/${id}/estado`,
      { body: {} },
    ),

  emitirNota: (
    id: number,
    datos: { codigo: string; motivo: string; serie: string },
  ): Promise<NotaEmitidaRespuesta> =>
    apiClient.post<NotaEmitidaRespuesta>(`/api/gestion/administracion/comprobantes/${id}/nota`, {
      body: datos,
    }),

  anular: (id: number, motivo: string): Promise<AnulacionRespuesta> =>
    apiClient.post<AnulacionRespuesta>(`/api/gestion/administracion/comprobantes/${id}/anular`, {
      body: { motivo },
    }),
};

/** Listado de comprobantes administrativos. */
export function useComprobantes(filtros: FiltrosComprobantes) {
  return useQuery({
    queryKey: comprobantesKeys.listado(filtros),
    queryFn: ({ signal }) => comprobantesApi.listar(filtros, signal),
    staleTime: 15_000,
  });
}

/** Detalle de un comprobante (venta, líneas y movimientos). */
export function useDetalleComprobante(id: number | null) {
  return useQuery({
    queryKey: comprobantesKeys.detalle(id),
    queryFn: ({ signal }) => comprobantesApi.detalle(id as number, signal),
    enabled: id !== null,
  });
}

/** Motivos y series del formulario de nota de crédito. */
export function useCatalogoNota(id: number | null) {
  return useQuery({
    queryKey: comprobantesKeys.catalogoNota(id),
    queryFn: ({ signal }) => comprobantesApi.catalogoNota(id as number, signal),
    enabled: id !== null,
    staleTime: 60_000,
  });
}

/** Aprueba comprobantes (los manda a la cola de SUNAT). */
export function useAprobarComprobantes() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (comprobantes: number[]) => comprobantesApi.aprobar(comprobantes),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Devuelve a la cola los comprobantes que quedaron en error. */
export function useRepararEstados() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: () => comprobantesApi.reparar(),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Actualiza los importes de una venta. */
export function useActualizarPrecio(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { detalles: LineaPrecio[]; total: number; retencion_porcentaje: number }) =>
      comprobantesApi.actualizarPrecio(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Consulta el estado del comprobante en el servicio de facturación. */
export function useConsultarEstado() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprobantesApi.consultarEstado(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Emite la nota de crédito de un comprobante. */
export function useEmitirNota(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { codigo: string; motivo: string; serie: string }) =>
      comprobantesApi.emitirNota(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Anula el comprobante. */
export function useAnularComprobante(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (motivo: string) => comprobantesApi.anular(id as number, motivo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

/** Catálogos del formulario «Generar comprobante». */
export function useCatalogosCrear(tipo: string) {
  return useQuery({
    queryKey: comprobantesKeys.catalogoCrear(tipo),
    queryFn: ({ signal }) => comprobantesApi.catalogosCrear(tipo, signal),
    staleTime: 60_000,
  });
}

/** Crea un comprobante administrativo. */
export function useCrearComprobante() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PeticionCrearComprobante) => comprobantesApi.crear(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'comprobantes'] });
    },
  });
}

